import { NextStep } from '../components/NextStep'
import React from 'react'
import { ConcreteFormStepProps } from './interfaces'
import {
    Box,
    Checkbox, FormControl,
    FormControlLabel, FormGroup, FormHelperText,
    FormLabel,
    Stack,
    Switch,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from '@mui/material'
import * as yup from 'yup'
import { useTranslation } from 'react-i18next'
import { Controller } from 'react-hook-form'
import { useStepForm } from './hooks'

export const IataAndCrs = ({ values, onSubmit, lead }: ConcreteFormStepProps) => {

    const { t } = useTranslation()

    const systems = ['Amadeus', 'Sabre', 'Travelport']

    const schema = systems.reduce(
        // define dynamic schema (GDS Systems)
        (accumulator: yup.AnyObjectSchema, systemName: string) => {

            return accumulator.shape({
                [`has${systemName}`]: yup.boolean(),
                [`${systemName.toLowerCase()}UserId`]: yup.string()
                    .when(`has${systemName}`, {
                        is: true,
                        then: (schema) => schema.required()
                    })
            })
        },
        // define static schema
        yup.object({
            hasIataNumber: yup.boolean(),
            iataNumber: yup.string().when('hasIataNumber', {
                is: true,
                then: (schema) => schema.required().matches(/^\d{7}$/)
            })
        })
    )

    const {
        control,
        handleSubmit,
        augmentedFieldProps,
        watch,
    } = useStepForm('IataAndCrs', schema, values, false)

    const watchedFields = systems
        .reduce((accumulator, systemName) => {
                return {
                    ...accumulator,
                    [`has${systemName}`]: watch(`has${systemName}`) ?? false
                }
            },
            {
                hasIataNumber: watch('hasIataNumber') ?? false
            }
        )
    const onSubmitWithDataTransform = (data: Record<string, any>) => {
        const patchData = systems.reduce((accumulator, systemName) => {
                return {
                    ...accumulator,
                    [`${systemName.toLowerCase()}UserId`]: data[`has${systemName}`] ? data[`${systemName.toLowerCase()}UserId`] : ''
                }
            },
            // Static Defaults
            {
                ...data,
                iataNumber: data.hasIataNumber ? data.iataNumber : ''
            }
        )
        onSubmit(patchData)
    }

    return <>
        <Typography mb={4} variant="h5" component="h1">{t('IataAndCrs.heading')}</Typography>
        <form onSubmit={handleSubmit(onSubmitWithDataTransform)}>
            <Stack maxWidth={500}>
                <Typography variant="h6" component="h2" mb={2}>{t('IataAndCrs.iataSection')}</Typography>

                <Controller
                    name="hasIataNumber"
                    control={control}
                    render={({ field }) =>
                        <FormControlLabel
                            label={augmentedFieldProps(field).label}
                            control={<Switch {...field} checked={!!field.value}/>}
                        />
                    }
                />

                <Box mt={1}>
                    <Controller
                        name="iataNumber"
                        control={control}
                        render={({ field }) => <TextField
                            sx={{ display: watchedFields.hasIataNumber ? '' : 'none' }}
                            {...augmentedFieldProps(field)}
                            fullWidth={true}
                        />}
                    />
                </Box>

                <Typography variant="h6" component="h2" my={2}>{t('IataAndCrs.reservationSystemsSection')}</Typography>

                <FormControl component="fieldset">
                    <FormGroup>
                        {systems.map((systemName, i) => {
                            return <Box mb={2} key={systemName}>
                                <Controller
                                    name={`has${systemName}`}
                                    control={control}
                                    render={({ field }) =>
                                        <FormControlLabel
                                            label={augmentedFieldProps(field).label}
                                            control={<Switch {...field} checked={!!field.value}/>}
                                        />
                                    }
                                />
                                <Box mt={1}>
                                    <Controller
                                        name={`${systemName.toLowerCase()}UserId`}
                                        control={control}
                                        defaultValue=""
                                        render={({ field }) => <TextField
                                            // @ts-ignore
                                            sx={{ display: watchedFields[`has${systemName}`] ? null : 'none' }}
                                            {...augmentedFieldProps(field)}
                                            fullWidth={true}
                                        />}
                                    />
                                </Box>
                            </Box>
                        })}
                    </FormGroup>
                </FormControl>
            </Stack>

            <NextStep/>
        </form>
    </>
}
